/* ==========================================================================
   Om Mushrooms — Brand World 2026 cart (wi-2026-0932, increment 3)
   --------------------------------------------------------------------------
   Rebuilt from the approved chrome wireframe (nate .../wireframes/chrome/chrome.js).
   ONE client-side model drives BOTH the cart DRAWER and the cart PAGE, so the
   free-shipping bar, the per-line subscribe state, the convert-all figure and the
   subtotal are one arithmetic and cannot drift apart (the wireframe's O-W-02).

   Why the whole cart body renders from the LIVE cart JSON in JS, not from Liquid:
   the program rule forbids a subscription price (or a discount % beside a price) in
   crawlable server-rendered HTML. The convert-all figure, the per-line savings and
   the converted prices are therefore computed here from /cart.js + the selling
   plan adjustment read from /products/<handle>.js — never printed into the markup.
   The one-time line prices ARE crawlable and appear; nothing subscription does.

   No jQuery. Reuses the theme globals when present (trapFocus / removeTrapFocus /
   onKeyUpEscape from global.js, PUB_SUB_EVENTS + subscribe/publish from pubsub.js,
   window.routes) and degrades safely when they are not.
   ========================================================================== */
(function () {
  "use strict";

  var routes = window.routes || {};
  var CART_URL = routes.cart_url || "/cart";
  var CHANGE_URL = routes.cart_change_url || "/cart/change.js";
  var MONEY_FORMAT = window.OMCartMoneyFormat || "${{amount}}";

  /* ---- money ------------------------------------------------------------- */
  function formatMoney(cents) {
    cents = Math.round(cents || 0);
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      try { return window.Shopify.formatMoney(cents, MONEY_FORMAT); } catch (e) {}
    }
    return "$" + (cents / 100).toFixed(2);
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /* ---- product-JSON cache: the selling plan lives here, never in the cart -- */
  var _productCache = {};
  var _productReady = {};   // handle -> product JSON (or null) once its fetch has settled
  function productData(handle) {
    if (!handle) return Promise.resolve(null);
    if (_productCache[handle]) return _productCache[handle];
    _productCache[handle] = fetch("/products/" + handle + ".js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (p) { _productReady[handle] = p; return p; });
    return _productCache[handle];
  }

  /* The "Subscribe & Save" selling plan for a product, read from its own JSON.
     The RATE is never hardcoded — it is the plan's own price_adjustment. */
  function subscribePlan(product) {
    if (!product || !product.selling_plan_groups || !product.selling_plan_groups.length) return null;
    var groups = product.selling_plan_groups;
    var group = null, i;
    for (i = 0; i < groups.length; i++) {
      if (/subscri/i.test(groups[i].name || "")) { group = groups[i]; break; }
    }
    if (!group) group = groups[0];
    if (!group.selling_plans || !group.selling_plans.length) return null;
    var plan = group.selling_plans[0];
    var adj = (plan.price_adjustments && plan.price_adjustments[0]) || null;
    return { id: plan.id, name: plan.name || "Subscription", adjustment: adj };
  }
  /* Apply the plan's adjustment to a per-item one-time price (cents) -> sub price. */
  function adjustedCents(otpCents, adj) {
    if (!adj) return otpCents;
    if (adj.value_type === "percentage") return Math.round(otpCents * (1 - Number(adj.value) / 100));
    if (adj.value_type === "fixed_amount") return Math.max(0, otpCents - Number(adj.value));
    if (adj.value_type === "price") return Number(adj.value);
    return otpCents;
  }

  /* ---- eligibility ------------------------------------------------------- *
     A line is convert-eligible iff it is a one-time line whose product carries a
     subscription selling plan (gift cards and bundles have none, so they fall out
     by construction). Ineligible lines say WHY when the reason is knowable. */
  function looksLikeBundle(line, product) {
    if (/bundle|kit|set/i.test((product && product.type) || "")) return true;
    var tags = (product && product.tags) || [];
    for (var i = 0; i < tags.length; i++) { if (/bundle|kit|set/i.test(tags[i])) return true; }
    var props = line.properties || {};
    if (props._bundle_id || props.bundle_id || props._bundle || props.bundle_src) return true;
    if (/bundle|kit|-set/i.test(line.handle || "")) return true;
    return false;
  }

  /* Build the full model for one /cart.js line (+ its product JSON). */
  function analyze(line, product) {
    var alloc = line.selling_plan_allocation;
    var isSub = !!alloc;
    var plan = subscribePlan(product);
    var perItemOtp = line.original_price;          // one-time per-item, in cents
    var lineOtp = line.original_line_price;         // one-time line total
    var m = {
      key: line.key,
      title: line.product_title || line.title,
      variantTitle: (line.variant_title && line.variant_title !== "Default Title") ? line.variant_title : "",
      image: line.image ? line.image.replace(/(\.[a-z]+)(\?|$)/i, "_120x$1$2") : "",
      url: line.url,
      qty: line.quantity,
      isSub: isSub,
      eligible: false,
      why: "",
      sellingPlanId: null,
      cadence: "",
      linePriceCents: line.final_line_price,        // what the line costs now
      lineSaveCents: 0
    };
    if (isSub) {
      m.eligible = true;
      m.cadence = (alloc.selling_plan && alloc.selling_plan.name) || (plan && plan.name) || "Subscription";
      m.sellingPlanId = alloc.selling_plan && alloc.selling_plan.id;
      // Shopify bakes the plan discount into every *_price field on a subscribed
      // line, so the saving is the allocation's own compare_at vs its price.
      var compAt = alloc.compare_at_price != null ? alloc.compare_at_price : alloc.price;
      m.lineSaveCents = Math.max(0, (compAt - alloc.price) * line.quantity);
      return m;
    }
    if (line.gift_card) { m.why = "Gift cards can’t subscribe."; return m; }
    if (plan) {
      m.eligible = true;
      m.sellingPlanId = plan.id;
      m.cadence = plan.name;
      var subPer = adjustedCents(perItemOtp, plan.adjustment);
      m.lineSaveCents = Math.max(0, (perItemOtp - subPer) * line.quantity);
      return m;
    }
    // one-time, no subscription available
    if (looksLikeBundle(line, product)) {
      m.why = "Bundles subscribe by their own path — not part of a convert-all.";
    }
    return m;
  }

  /* Model a cart with whatever product JSON has already arrived (synchronous). A line whose
     product is still in flight models as plain one-time; lines, prices and subtotal are exact. */
  function modelNow(cart) {
    var lines = cart.items.map(function (l) { return analyze(l, _productReady[l.handle] || null); });
    return { cart: cart, lines: lines };
  }
  function cartHandles(cart) {
    var handles = {};
    cart.items.forEach(function (l) { if (l.handle) handles[l.handle] = 1; });
    return Object.keys(handles);
  }
  /* Model a cart once every distinct product JSON it needs has arrived. */
  function modelFromCart(cart) {
    return Promise.all(cartHandles(cart).map(productData)).then(function () { return modelNow(cart); });
  }
  /* Fetch the cart and every distinct product JSON it needs, then model it. */
  function loadModel() {
    return fetch(CART_URL + ".js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(modelFromCart);
  }

  /* ---- arithmetic (all in cents, rounded per line) ----------------------- */
  var FREE_SHIP_CENTS = Number(window.OMFreeShipThreshold || 4500);
  function convertible(model) {
    return model.lines.filter(function (l) { return l.eligible && !l.isSub; });
  }
  function convertSavingCents(model) {
    return convertible(model).reduce(function (t, l) { return t + l.lineSaveCents; }, 0);
  }
  function subtotalCents(model) {
    return model.lines.reduce(function (t, l) { return t + l.linePriceCents; }, 0);
  }

  /* ---- render fragments (L37 v9, wi-2026-1120) ---------------------------
     The markup is the locked lego's: the shipping bar is the Thread (a track,
     a heavier travelled segment, two dots; no percentage drawn or written), a
     line is a 72px 3:4 thumb + title + the subscribe switch + the stepper and
     price, and the convert-all callout states the money. Styles live in
     assets/om-chrome.css (.omc). */
  /* wi-2026-1293: true when the cart holds a SKU that ships free at any value. */
  function hasAlwaysFreeSku(model) {
    var skus = window.OMAlwaysFreeShipSkus || [];
    if (!skus.length) return false;
    return ((model.cart && model.cart.items) || []).some(function (i) {
      return skus.indexOf(String(i.sku || "")) > -1;
    });
  }
  function shipBarHTML(model) {
    var sub = subtotalCents(model), thr = FREE_SHIP_CENTS;
    var thrLabel = formatMoney(thr).replace(".00", "");
    var always = hasAlwaysFreeSku(model);
    var frac = always ? 1 : Math.max(0, Math.min(1, thr ? sub / thr : 1));
    var copy = always
      ? 'Free shipping <span class="om-soft">&middot; this cart ships free</span>'
      : sub >= thr
      ? 'Free shipping <span class="om-soft">&middot; this cart is over the ' + esc(thrLabel) + " threshold</span>"
      : "Add " + esc(formatMoney(thr - sub)) + ' more for free shipping <span class="om-soft">&middot; orders over ' + esc(thrLabel) + " ship free</span>";
    return (
      '<div class="track om-thread--x" aria-hidden="true" style="--omc-fill:' + frac.toFixed(3) + '">' +
        '<span class="om-thread-dot"></span><span class="fill"></span><span class="om-thread-dot"></span>' +
      "</div>" +
      '<p class="ship om-small" data-lp="shipping">' + copy + "</p>"
    );
  }

  function lineHTML(l) {
    var plan = l.isSub ? "Subscribe &amp; Save" : "One-time";
    var title = esc(l.title) + (l.variantTitle ? " &middot; " + esc(l.variantTitle) : "") + " &middot; " + plan;
    var swap = "";
    if (l.eligible) {
      swap = '<button class="swap" type="button" role="switch" aria-checked="' + (l.isSub ? "true" : "false") +
        '" data-lp="subscribe_swap" data-om-swap="' + esc(l.key) + '">' +
        '<span class="swap-track" aria-hidden="true"></span><span>' +
        (l.isSub ? "Subscribe &amp; save &middot; " + esc(l.cadence) : "Switch to Subscribe &amp; save") + "</span></button>";
    }
    var why = l.why ? '<p class="line-note om-small">' + esc(l.why) + "</p>" : "";
    var save = (l.isSub && l.lineSaveCents > 0)
      ? '<p class="line-note om-small">Saving ' + formatMoney(l.lineSaveCents) + " on this item</p>" : "";
    var media = l.image
      ? '<img class="thumb" src="' + esc(l.image) + '" alt="" width="72" height="96" loading="lazy">'
      : '<span class="thumb" aria-hidden="true"></span>';
    return (
      '<div class="line" data-om-line="' + esc(l.key) + '">' +
        '<a href="' + esc(l.url) + '" tabindex="-1" aria-hidden="true">' + media + "</a>" +
        '<div class="line-body">' +
          '<a class="om-subhead om-subhead--sm line-title" href="' + esc(l.url) + '" data-lp="line_title">' + title + "</a>" +
          swap + why + save +
          '<div class="line-foot">' +
            '<div class="qty" role="group" aria-label="Quantity, ' + esc(l.title) + '">' +
              '<button class="step" type="button" data-om-qty="' + esc(l.key) + '" data-om-delta="-1" aria-label="' + (l.qty > 1 ? "Decrease quantity" : "Remove " + esc(l.title)) + '">&minus;</button>' +
              '<span class="count om-num" aria-live="polite">' + l.qty + "</span>" +
              '<button class="step" type="button" data-om-qty="' + esc(l.key) + '" data-om-delta="1" aria-label="Increase quantity">+</button>' +
            "</div>" +
            '<span class="price om-num om-subhead om-subhead--sm" data-lp="line_price">' + formatMoney(l.linePriceCents) + "</span>" +
          "</div>" +
        "</div>" +
      "</div>"
    );
  }
  function linesHTML(model) {
    if (!model.lines.length) return '<p class="empty om-small">Your cart is empty. <a href="/collections/all">Shop all</a></p>';
    return model.lines.map(lineHTML).join("");
  }

  /* The convert-all callout. Rendered ONLY when an eligible one-time line exists.
     When the whole eligible set is already subscribed it shows the "saving / undo"
     state. Both the figure and the state are client-derived, never in the HTML. */
  function convertHTML(model) {
    var set = convertible(model);
    var subbed = model.lines.filter(function (l) { return l.isSub && l.eligible; });
    var freedoms = '<p class="om-small om-soft--deep">Free shipping on every renewal &middot; <span class="nobreak">100-day</span> guarantee &middot; pause, skip or cancel anytime</p>';
    if (set.length) {
      return '<button class="om-btn om-btn--secondary om-btn--block" type="button" data-cart-convert data-om-convert-all>' +
        "Switch everything to Subscribe &amp; Save and save " + formatMoney(convertSavingCents(model)) + "</button>" + freedoms;
    }
    if (subbed.length) {
      var saved = subbed.reduce(function (t, l) { return t + l.lineSaveCents; }, 0);
      return '<p class="om-subhead om-subhead--sm">You are saving ' + formatMoney(saved) + " with Subscribe &amp; Save</p>" +
        freedoms + '<button type="button" class="undo" data-om-undo>Back to one-time</button>';
    }
    return "";
  }

  /* ---- Cart API mutations ------------------------------------------------ */
  function change(body) {
    return fetch(CHANGE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }
  function publishUpdate(cart) {
    if (typeof window.publish === "function" && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: "om-cart", cartData: cart });
    }
  }

  /* ---- Elevar / dataLayer (lane H rows 11) ------------------------------- */
  function elevarPush(envelope) {
    if (window.ElevarDataLayer && typeof window.ElevarDataLayer.push === "function") {
      window.ElevarDataLayer.push(envelope);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(envelope);
    }
  }
  function fireViewCart(cart) {
    var products = (cart.items || []).map(function (l) {
      return {
        id: l.sku || String(l.variant_id), name: l.product_title || l.title,
        product_id: String(l.product_id), variant_id: String(l.variant_id),
        brand: (l.vendor || "OM Mushrooms"), price: (l.final_price / 100).toFixed(2),
        quantity: String(l.quantity)
      };
    });
    elevarPush({
      event: "dl_view_cart",
      cart_total: (cart.total_price / 100).toFixed(2),
      ecommerce: { currencyCode: cart.currency, actionField: { list: "Shopping Cart" }, impressions: products }
    });
  }
  /* No manual dl_remove_from_cart: Elevar's cart listener emits one per /cart/change,
     so a manual push doubled a full removal (O9 red team #7 follow-up). */

  /* ==========================================================================
     THE SHARED SURFACE RENDERER — used by the drawer AND the cart page.
     A surface is any element carrying the mount points below.
     ======================================================================== */
  function paint(root, model) {
    var slot = function (sel) { return root.querySelector(sel); };
    var ship = slot("[data-om-shipbar]"); if (ship) ship.innerHTML = shipBarHTML(model);
    var lines = slot("[data-om-lines]"); if (lines) lines.innerHTML = linesHTML(model);
    var conv = slot("[data-om-convert]"); if (conv) conv.innerHTML = convertHTML(model);
    var sub = slot("[data-om-subtotal]"); if (sub) sub.textContent = formatMoney(subtotalCents(model));
    root.classList.toggle("is-empty", !model.lines.length);
    // header count bubble
    var n = model.cart.item_count;
    var bubble = document.querySelector(".cart-count-bubble");
    if (bubble) {
      bubble.textContent = n < 100 ? n : "99+";
      bubble.hidden = !n;
    }
    var link = document.getElementById("cart-icon-bubble");
    if (link) link.setAttribute("aria-label", "Cart, " + n + (n === 1 ? " item" : " items"));
  }

  /* Every paint takes a generation number, so a slower, older fetch can never paint over a
     newer cart (wi-2026-1204: a refresh already in flight when an accept lands). */
  function refresh(root) {
    var gen = root._omGen = (root._omGen || 0) + 1;
    return loadModel().then(function (model) {
      if (gen !== root._omGen) return root._omModel;
      root._omModel = model;
      paint(root, model);
      publishUpdate(model.cart);
      return model;
    });
  }
  /* Paint a cart we already hold (the response of the write that produced it) with NO fetch:
     lines, subtotal and the ship bar are right the moment this returns. Lines whose product JSON
     is not cached yet get their subscribe switch / note on a second paint when it arrives. */
  function paintCart(root, cart) {
    var gen = root._omGen = (root._omGen || 0) + 1;
    var model = modelNow(cart);
    root._omModel = model;
    paint(root, model);
    publishUpdate(cart);
    var pending = cartHandles(cart).some(function (h) { return !(h in _productReady); });
    if (!pending) return Promise.resolve(model);
    return modelFromCart(cart).then(function (full) {
      if (gen !== root._omGen) return root._omModel;
      root._omModel = full;
      paint(root, full);
      return full;
    });
  }

  /* Wire the delegated controls once per surface. */
  function wire(root) {
    if (root._omWired) return;
    root._omWired = true;
    root.addEventListener("click", function (e) {
      var btn = e.target.closest("button");
      if (!btn || !root.contains(btn)) return;
      var model = root._omModel;

      if (btn.hasAttribute("data-om-promo")) {
        e.preventDefault();
        var inp = root.querySelector("[data-om-promo-input]");
        var code = inp && inp.value.trim();
        // /discount/<code> applies the code to the session cart, then returns to /cart
        if (code) window.location.href = "/discount/" + encodeURIComponent(code) + "?redirect=" + encodeURIComponent(CART_URL);
        return;
      }
      if (btn.hasAttribute("data-om-convert-all")) {
        e.preventDefault(); busy(root, true);
        var set = convertible(model);
        seq(set.map(function (l) { return function () { return change({ id: l.key, quantity: l.qty, selling_plan: l.sellingPlanId }); }; }))
          .then(function () { return commit(root); }).then(function () { busy(root, false); });
        return;
      }
      if (btn.hasAttribute("data-om-undo")) {
        e.preventDefault(); busy(root, true);
        var subbed = model.lines.filter(function (l) { return l.isSub && l.eligible; });
        seq(subbed.map(function (l) { return function () { return change({ id: l.key, quantity: l.qty, selling_plan: null }); }; }))
          .then(function () { return commit(root); }).then(function () { busy(root, false); });
        return;
      }
      if (btn.hasAttribute("data-om-swap")) {
        e.preventDefault(); busy(root, true);
        var line = find(model, btn.getAttribute("data-om-swap"));
        // the switch toggles: on = the product's own plan, off = back to one-time
        change({ id: line.key, quantity: line.qty, selling_plan: line.isSub ? null : line.sellingPlanId })
          .then(function () { return commit(root); }).then(function () { busy(root, false); });
        return;
      }
      if (btn.hasAttribute("data-om-qty")) {
        e.preventDefault();
        var l = find(model, btn.getAttribute("data-om-qty"));
        var q = l.qty + Number(btn.getAttribute("data-om-delta"));
        if (q < 0) q = 0; busy(root, true);
        // L37 v9 has no separate Remove control: stepping to zero removes the
        // line; Elevar's own cart listener emits dl_remove_from_cart for it.
        change({ id: l.key, quantity: q }).then(function () { return commit(root); }).then(function () { busy(root, false); });
        return;
      }
      if (btn.hasAttribute("data-om-remove")) {
        e.preventDefault();
        var rl = find(model, btn.getAttribute("data-om-remove"));
        busy(root, true);
        change({ id: rl.key, quantity: 0 }).then(function () { return commit(root); }).then(function () { busy(root, false); });
        return;
      }
    });
  }
  /* Drawer: repaint in place. Cart PAGE: reload, so the server-rendered footer
     subtotal (a different section) can never fall out of step with the lines. */
  function commit(root) {
    if (root.tagName === "CART-PAGE-ITEMS") { window.location.reload(); return Promise.resolve(); }
    return refresh(root);
  }
  function find(model, key) { return model.lines.filter(function (l) { return l.key === key; })[0]; }
  function busy(root, on) { root.classList.toggle("omc-busy", !!on); }
  function seq(fns) { return fns.reduce(function (p, f) { return p.then(f); }, Promise.resolve()); }

  /* ==========================================================================
     <cart-drawer> — the drawer shell AND the drawer's paint/wire SURFACE ROOT.
     Its mount points (ship bar, lines, convert-all, subtotal) and its controls
     span the header, <cart-drawer-items> and the footer, so the drawer ELEMENT
     (their common ancestor) is the root — not <cart-drawer-items>, which only
     holds the scrollable lines + the persistent upsell card.
     ======================================================================== */
  if (!customElements.get("cart-drawer")) {
    customElements.define("cart-drawer", class extends HTMLElement {
      constructor() {
        super();
        this._loaded = false; this._loading = null;
        this.addEventListener("keyup", function (e) { if (e.code === "Escape") this.close(); }.bind(this));
        var overlay = this.querySelector("[data-om-overlay]");
        if (overlay) overlay.addEventListener("click", this.close.bind(this));
        wire(this);
        this.bindTrigger();
      }
      bindTrigger() {
        var link = document.querySelector("#cart-icon-bubble");
        if (!link) return;
        link.setAttribute("role", "button");
        link.setAttribute("aria-haspopup", "dialog");
        var self = this;
        link.addEventListener("click", function (e) { e.preventDefault(); self.open(link); });
        link.addEventListener("keydown", function (e) {
          if (e.code && e.code.toUpperCase() === "SPACE") { e.preventDefault(); self.open(link); }
        });
      }
      // fetch + paint once against THIS element; open() awaits it
      ensureFresh() {
        if (this._loaded) return Promise.resolve(this._omModel);
        if (this._loading) return this._loading;
        var self = this;
        this._loading = refresh(this).then(function (m) { self._loaded = true; self._loading = null; return m; });
        return this._loading;
      }
      // reload(cart): bundle-upsell hands over the cart its own write returned, so the drawer
      // paints it now instead of re-fetching (wi-2026-1204). No cart => the old fetch path.
      reload(cart) {
        if (cart && cart.items) {
          var self = this;
          this._loading = null; this._loaded = true;
          return paintCart(this, cart).then(function (m) { return m || self._omModel; });
        }
        this._loaded = false; this._loading = null; return this.ensureFresh();
      }
      // Legacy-caller shim (QA fix, coffee-booster PDP still on the old Dawn
      // main-product/buy_buttons path): assets/product-form.js and
      // assets/bundle-upsell-surface.js duck-type `typeof cart.renderContents
      // === "function"` after a successful /cart/add.js POST to decide whether
      // they can hand off to the drawer at all. The wi-2026-0932 rebuild
      // dropped this method, so on any page still using <product-form> the
      // check failed and the ATC handler fell into its "cart methods aren't
      // available" branch (`window.location = routes.cart_url`) instead of
      // opening the drawer — the drawer element itself was never broken, it
      // was just never asked to open. `parsedState` (the /cart/add.js
      // response) is intentionally unused: this drawer always repaints from
      // its own fresh /cart.js fetch (see refresh()), never from the add
      // response, so reload()+open() reproduces the old contract's effect
      // (repaint + open) without reviving the old sections-based render path.
      // The new family PDP buy box (sections/pdp-buy-box.liquid) never calls
      // this — it calls .open() directly — so that path is unchanged.
      renderContents(parsedState) {
        return this.reload().then(this.open.bind(this));
      }
      open(triggeredBy) {
        this.activeElement = triggeredBy || document.activeElement;
        var self = this;
        this.ensureFresh().then(function () {
          self.classList.add("active");
          document.body.classList.add("overflow-hidden");
          var focusEl = self.querySelector(".omc-panel");
          if (typeof window.trapFocus === "function" && focusEl) window.trapFocus(focusEl, focusEl);
          // the drawer is one long scroll (L37 v6): it always opens at the top,
          // whatever a checkout-app widget inside the foot does with focus
          if (focusEl) { focusEl.scrollTop = 0; setTimeout(function () { focusEl.scrollTop = 0; }, 300); }
          if (self._omModel) fireViewCart(self._omModel.cart);
        });
      }
      close() {
        this.classList.remove("active");
        if (typeof window.removeTrapFocus === "function") window.removeTrapFocus(this.activeElement);
        document.body.classList.remove("overflow-hidden");
      }
    });
  }

  /* <cart-drawer-items> — the scroll container for the lines + the persistent
     upsell mount (bundle-upsell-card, D-051, lane H row 27). It renders nothing
     itself; when the cart changes from ANOTHER surface (a PDP add), it asks the
     parent drawer to re-fetch and open. */
  if (!customElements.get("cart-drawer-items")) {
    customElements.define("cart-drawer-items", class extends HTMLElement {
      connectedCallback() {
        if (window.PUB_SUB_EVENTS && typeof window.subscribe === "function") {
          var self = this;
          this._unsub = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (evt) {
            if (evt && evt.source === "om-cart") return;  // our own write — already painted
            var drawer = self.closest("cart-drawer");
            if (drawer) drawer.reload().then(function () { drawer.open(); });
          });
        }
      }
      disconnectedCallback() { if (this._unsub) this._unsub(); }
    });
  }

  /* <cart-page-items> — the cart PAGE surface. Same renderer, same arithmetic. */
  if (!customElements.get("cart-page-items")) {
    customElements.define("cart-page-items", class extends HTMLElement {
      connectedCallback() {
        wire(this);
        var self = this;
        // No manual dl_view_cart here: Elevar emits view_cart on the /cart page load itself, so this doubled
        // GA4 view_cart (O9, 2026-09-30). The drawer-open fireViewCart stays: Elevar sends none on a drawer open.
        refresh(this);
        if (window.PUB_SUB_EVENTS && typeof window.subscribe === "function") {
          this._unsub = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (evt) {
            if (evt && evt.source === "om-cart") return;
            refresh(self);
          });
        }
      }
      // Public repaint hook (wi-2026-1204, wi-2026-1216): bundle-upsell calls this after an accept with
      // the cart its write returned. Paint it in place and stop: every cart-dependent node on /cart is
      // painted here (ship bar, lines, convert-all, subtotal), and the only server-rendered one (express
      // checkout) shows whenever the cart has items, which an accept never takes to zero. The old full
      // reload flashed the no-JS cart form and re-ran page-load cart writers (Polar), which doubled
      // GA4's remove_from_cart. Without a usable cart, fall back to the page's own reload path.
      reload(cart) {
        if (cart && cart.items && cart.item_count > 0) return paintCart(this, cart);
        var self = this;
        return refresh(self).then(function () { commit(self); });
      }
      disconnectedCallback() { if (this._unsub) this._unsub(); }
    });
  }

  /* ---- owned checkout (wi-2026-1222) -------------------------------------
     A shopper signed in with Shop who reaches /checkout is redirected by
     Shopify to the Shop-hosted checkout on shop.app, which is off our domain
     and runs without our branding and checkout extensions, and resets
     attribution. Shopify documents no merchant toggle, so the drawer and
     /cart Checkout buttons go to /checkout?skip_shop_pay=true instead. Shop
     Pay is still offered as a payment method inside our checkout. The express
     buttons (Buy with Shop / Apple Pay / Google Pay) are deliberately left
     alone (Nate, 2026-10-02).
     The /cart form posts quantity inputs (updates[]); any pending edit is saved
     with /cart/update.js first so the cart reaches checkout exactly as shown.
     Cart attributes and note live server-side on the cart and need no carry.
     If JS fails, the native form POST still reaches checkout. -------------- */
  var OWNED_CHECKOUT_URL = (routes.root_url || "/").replace(/\/$/, "") + "/checkout?skip_shop_pay=true";
  document.addEventListener("submit", function (e) {
    var form = e.target;
    var submitter = e.submitter;
    var isDrawer = form && form.id === "omc-checkout-form";
    var isCartPage = form && form.id === "cart" && submitter && submitter.name === "checkout";
    if (!isDrawer && !isCartPage) return;
    if (submitter && submitter.name && submitter.name !== "checkout") return;
    e.preventDefault();
    var go = function () { window.location.href = OWNED_CHECKOUT_URL; };
    if (!isCartPage) { go(); return; }
    var body = new FormData(form);
    body.delete("checkout");
    fetch((routes.cart_update_url || "/cart/update") + ".js", { method: "POST", body: body, headers: { Accept: "application/json" } })
      .then(go, go);
  });
})();
